/**
 * 레슨 관련 API 함수
 */

import type { CourseDetailDto, LessonDetailDto } from '@repo/types';
import type { LessonKind } from '@theailab-dev/parchment';
import type { AxiosResponse } from 'axios';
import apiClient from '../lib/apiClient';

/**
 * 레슨 상세 조회
 *
 * 엔드포인트: GET /api/lesson/:id
 * - 레슨 기본 정보 (제목, 설명, 동영상 URL 등)
 * - 소속 챕터 정보
 * - 소속 강의 정보
 *
 * 흐름:
 * 1. 레슨 ID를 path parameter로 전달
 * 2. GET /lesson/:id 요청
 * 3. 서버에서 레슨 상세 정보 반환
 * 4. LessonDetailDto 반환 (챕터 및 강의 정보 포함)
 *
 * @param lessonId - 레슨 ID
 * @returns 레슨 상세 정보 (챕터, 강의 정보 포함)
 * @throws API 요청 실패 시 에러 (404: 레슨 없음 등)
 */
export const getLessonDetail = async (lessonId: number): Promise<LessonDetailDto> => {
  try {
    console.log('📤 레슨 상세 조회 요청:', { lessonId });

    const response = await apiClient.get<LessonDetailDto>(`/course/lesson/${lessonId}`);

    console.log('📚 레슨 상세 조회 성공:', {
      id: response.data.id,
      title: response.data.title,
      챕터: response.data.chapter?.title,
      강의: response.data.course?.title,
    });
    return response.data;
  } catch (error) {
    console.error('❌ 레슨 상세 조회 실패:', error);
    throw error;
  }
};

/**
 * 레슨 생성 요청 DTO
 */
export interface CreateLessonDto {
  chapterId: number;
  title: string;
  kind: LessonKind;
  seq?: number;
  duration?: string | null;
  isPreviewable?: boolean;
  startAt?: Date | null;
  endAt?: Date | null;
  content?: string | null;
  videoUrl?: string | null;
  materialsUrl?: string | null;
  joinUrl?: string | null;
}

/**
 * 레슨 수정 요청 DTO
 */
export interface UpdateLessonDto {
  title?: string;
  kind?: LessonKind;
  duration?: string | null;
  isPreviewable?: boolean;
  startAt?: Date | null;
  endAt?: Date | null;
  content?: string | null;
  contentUrl?: string | null;
  videoUrl?: string | null;
  materialsUrl?: string | null;
  joinUrl?: string | null;
}

/**
 * 레슨 생성
 *
 * 엔드포인트: POST /api/course/chapter/:chapterId/lesson
 * - 필수 필드: title, kind, seq
 * - 선택 필드: duration, isPreviewable, startAt, content, videoUrl, materialsUrl, joinUrl
 * - VIDEO 타입일 경우 video 파일을 FormData로 함께 전송
 *
 * @param chapterId - 챕터 ID
 * @param data - 레슨 생성 데이터
 * @param videoFile - 동영상 파일 (VIDEO 타입일 경우 선택)
 * @param materialFile - 레슨 자료 파일 (선택)
 * @returns 레슨 상세 정보
 * @throws API 요청 실패 시 에러
 */
export const createLesson = async (
  chapterId: number,
  data: Omit<CreateLessonDto, 'chapterId'>,
  videoFile?: File,
  materialFile?: File,
  onUploadProgress?: (progressEvent: any) => void,
): Promise<LessonDetailDto> => {
  try {
    console.log('📤 레슨 생성 요청:', {
      chapterId,
      data,
      hasVideoFile: !!videoFile,
      hasMaterialFile: !!materialFile,
    });

    let response: AxiosResponse<LessonDetailDto>;
    const useFormData = videoFile || materialFile;

    if (useFormData) {
      const formData = new FormData();
      formData.append('title', data.title);
      formData.append('kind', data.kind);
      if (data.seq !== undefined) {
        formData.append('seq', data.seq.toString());
      }
      if (data.duration !== undefined && data.duration !== null) {
        formData.append('duration', data.duration);
      }
      if (data.isPreviewable !== undefined) {
        formData.append('isPreviewable', data.isPreviewable.toString());
      }
      if (data.startAt) {
        formData.append('startAt', data.startAt instanceof Date ? data.startAt.toISOString() : data.startAt);
      }
      if (data.endAt) {
        formData.append('endAt', data.endAt instanceof Date ? data.endAt.toISOString() : data.endAt);
      }
      if (data.content) {
        formData.append('content', data.content);
      }
      if (data.joinUrl) {
        formData.append('joinUrl', data.joinUrl);
      }
      if (data.materialsUrl !== undefined) {
        formData.append('materialsUrl', data.materialsUrl ?? '');
      }
      if (videoFile) {
        formData.append('video', videoFile);
      }
      if (materialFile) {
        formData.append('material', materialFile);
      }

      response = await apiClient.post<LessonDetailDto>(`/course/chapter/${chapterId}/lesson`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 0, // 동영상 업로드 시 타임아웃 무제한
        onUploadProgress,
      });
    } else {
      response = await apiClient.post<LessonDetailDto>(`/course/chapter/${chapterId}/lesson`, data);
    }

    console.log('✅ 레슨 생성 성공:', {
      chapterId,
      lessonId: response.data.id,
      title: data.title,
      hasVideoFile: !!videoFile,
      hasMaterialFile: !!materialFile,
    });
    return response.data;
  } catch (error) {
    console.error('❌ 레슨 생성 실패:', error);
    throw error;
  }
};

/**
 * 레슨 수정
 *
 * 엔드포인트: PATCH /api/lesson/:lessonId
 * - 부분 업데이트 지원
 * - VIDEO 타입일 경우 video 파일을 FormData로 함께 전송
 *
 * @param lessonId - 레슨 ID
 * @param data - 레슨 수정 데이터
 * @param videoFile - 동영상 파일 (선택)
 * @param materialFile - 레슨 자료 파일 (선택)
 * @returns 레슨 상세 정보
 * @throws API 요청 실패 시 에러
 */
export const updateLesson = async (
  lessonId: number,
  data: UpdateLessonDto,
  videoFile?: File,
  materialFile?: File,
  onUploadProgress?: (progressEvent: any) => void,
): Promise<LessonDetailDto> => {
  try {
    console.log('📤 레슨 수정 요청:', {
      lessonId,
      data,
      hasVideoFile: !!videoFile,
      hasMaterialFile: !!materialFile,
    });

    let response: AxiosResponse<LessonDetailDto>;
    const useFormData = videoFile || materialFile;

    if (useFormData) {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value === undefined) return;
        const str =
          value === null
            ? ''
            : value instanceof Date
              ? value.toISOString()
              : typeof value === 'boolean'
                ? value
                  ? 'true'
                  : 'false'
                : String(value);
        formData.append(key, str);
      });
      if (videoFile) {
        formData.append('video', videoFile);
      }
      if (materialFile) {
        formData.append('material', materialFile);
      }

      response = await apiClient.patch<LessonDetailDto>(`/course/lesson/${lessonId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 0,
        onUploadProgress,
      });
    } else {
      response = await apiClient.patch<LessonDetailDto>(`/course/lesson/${lessonId}`, data);
    }

    console.log('✅ 레슨 수정 성공:', {
      lessonId,
      hasVideoFile: !!videoFile,
      hasMaterialFile: !!materialFile,
    });
    return response.data;
  } catch (error) {
    console.error('❌ 레슨 수정 실패:', error);
    throw error;
  }
};

/**
 * 레슨 삭제
 *
 * 엔드포인트: DELETE /api/course/lesson/:id
 *
 * @param lessonId - 레슨 ID
 * @returns 강의 상세 정보 (업데이트된 레슨 목록 포함)
 * @throws API 요청 실패 시 에러
 */
export const deleteLesson = async (lessonId: number): Promise<CourseDetailDto> => {
  try {
    console.log('📤 레슨 삭제 요청:', { lessonId });

    const response = await apiClient.delete<CourseDetailDto>(`/course/lesson/${lessonId}`);

    console.log('✅ 레슨 삭제 성공:', {
      lessonId,
    });
    return response.data;
  } catch (error) {
    console.error('❌ 레슨 삭제 실패:', error);
    throw error;
  }
};

/**
 * 레슨 동영상 파일 업로드
 *
 * 엔드포인트: POST /api/lesson/:lessonId/video
 * - formData로 파일 업로드
 *
 * @param lessonId - 레슨 ID
 * @param file - 업로드할 동영상 파일
 * @returns 레슨 상세 정보 (업로드된 동영상 URL 포함)
 * @throws API 요청 실패 시 에러
 */
export const uploadLessonVideo = async (
  lessonId: number,
  file: File,
  onUploadProgress?: (progressEvent: any) => void,
): Promise<LessonDetailDto> => {
  try {
    console.log('📤 레슨 동영상 업로드 요청:', {
      lessonId,
      fileName: file.name,
    });

    const formData = new FormData();
    formData.append('video', file);

    const response = await apiClient.post<LessonDetailDto>(`/course/lesson/${lessonId}/video`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0, // 동영상 업로드 시 타임아웃 무제한
      onUploadProgress,
    });

    console.log('✅ 레슨 동영상 업로드 성공:', {
      lessonId,
      fileName: file.name,
    });
    return response.data;
  } catch (error) {
    console.error('❌ 레슨 동영상 업로드 실패:', error);
    throw error;
  }
};

/**
 * 레슨 교안 파일 업로드
 *
 * 엔드포인트: POST /api/lesson/:lessonId/material
 * - formData로 파일 업로드
 *
 * @param lessonId - 레슨 ID
 * @param file - 업로드할 교안 파일
 * @returns 레슨 상세 정보 (업로드된 교안 URL 포함)
 * @throws API 요청 실패 시 에러
 */
export const uploadLessonMaterial = async (
  lessonId: number,
  file: File,
  onUploadProgress?: (progressEvent: any) => void,
): Promise<LessonDetailDto> => {
  try {
    console.log('📤 레슨 교안 업로드 요청:', { lessonId, fileName: file.name });

    const formData = new FormData();
    formData.append('material', file);

    const response = await apiClient.post<LessonDetailDto>(`/course/lesson/${lessonId}/material`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 0, // 파일 업로드 시 타임아웃 무제한
      onUploadProgress,
    });

    console.log('✅ 레슨 교안 업로드 성공:', {
      lessonId,
      fileName: file.name,
    });
    return response.data;
  } catch (error) {
    console.error('❌ 레슨 교안 업로드 실패:', error);
    throw error;
  }
};
